/**
 * Portal types withheld from search engines.
 *
 * ONE list, two consumers, because the two halves only work together:
 *
 *   - NoIndexRoutes.jsx puts `noindex,follow` on the rendered page. That is
 *     what actually removes a URL from the index.
 *   - customizations/helpers/Sitemap/Sitemap.js drops the same types from
 *     sitemap.xml.gz. That is what stops us advertising them.
 *
 * Splitting the list would mean advertising pages we ask Google to drop, or
 * dropping pages we still advertise. Neither is a state worth being able to
 * reach, so the list lives here and both sides import it.
 *
 * THE 08-15 CLIFF (investigated 2026-09-20)
 *
 * A glossary term's entire unique content is one sentence, and the page prints
 * it twice: `description` and `definition_1` hold the same string. Measured on
 * /glossary/random-testing: 18 words, duplicated. Directives holds 3,614 of
 * them (2,564 Definition + 1,050 published Acronym) and they were 3,615 of the
 * 7,255 URLs that site submitted to Google on 2026-08-11. Standards and nnsa
 * hold zero objects of either type.
 *
 * On 2026-08-16, after a four-day crawl surge, Googlebot cut its crawl of
 * directives from 2,768 hits a day to 215 and has not recovered. That site's
 * weekly Google clicks went 104, 131, 129, 3, 2, 4, 5 while the other two kept
 * growing. The loss is site-wide and proportional across every section,
 * rankings held while impressions vanished, and URL Inspection names the
 * mechanism: pages that were earning clicks on 08-13 now report "Crawled -
 * currently not indexed". Index coverage on /documents/, same day:
 *
 *     directives  16.7% indexed     standards  83.3%     nnsa  90.0%
 *
 * The thin-glossary mass is the only structural difference between the site
 * that collapsed and the two that did not. That is a strong correlation, not a
 * proof -- Google never states a reason -- so both halves are written to be
 * reverted by editing this one list back to empty.
 *
 * BOTH glossary types, not just Definition. DocumentSEO.jsx already treats the
 * pair as one thing (both map to schema.org DefinedTerm), and excluding only
 * Definition would leave 1,050 Acronym URLs advertised, including the 137
 * duplicate `copy_of_*` URLs of BL-0139, which are Acronym objects.
 *
 * NOT a robots.txt rule, and the distinction is load-bearing. The glossary IS
 * indexed (1,811 impressions in the week of 2026-08-06). A crawler forbidden
 * to FETCH a URL never sees the noindex that would remove it, so a Disallow
 * here would freeze these pages in the index permanently. They must stay
 * crawlable for exactly as long as it takes Google to read the tag.
 * backend/scripts/seo/set_robots.py records the same lesson at length, from
 * the wildcard login rule it had to withdraw on 2026-08-17 for this reason.
 */

export const NOINDEX_TYPES = ['Definition', 'Acronym'];

const NOINDEX_TYPE_SET = new Set(NOINDEX_TYPES);

export const shouldNoIndexType = (portalType) =>
  !!portalType && NOINDEX_TYPE_SET.has(portalType);

/**
 * The exclusion as plone.restapi query-string parameters, ready to append.
 *
 * Returns '' when nothing is excluded, so an empty NOINDEX_TYPES leaves the
 * upstream query byte-for-byte unchanged.
 *
 * `portal_type.not` is honoured under `use_site_search_settings=1` only
 * because the backend makes it so: upstream restapi reduces the whole
 * `portal_type` term to a plain list of user-friendly types and drops the
 * exclusion, answering zero results. See `_NotAwareSearchHandler` in
 * backend/src/dox/policyportal/core/services/querystring_search.py.
 */
export const noIndexTypesQuery = () =>
  NOINDEX_TYPES.map(
    (t) => `&portal_type.not:list=${encodeURIComponent(t)}`,
  ).join('');
